/**
 * useSourceVerification — load + run F6 legal source verification (F6-6).
 *
 * GET seeds the verdict list when the view becomes active. `run()` POSTs a
 * fresh batch (lawyer/reviewer), then refetches so the list reflects the new
 * conclusions. Partial-success (ADR-114 D7): a run can succeed overall while
 * some citations degraded — the summary carries `failedCount` / `failures`.
 *
 * The GET endpoint can't distinguish "never run" from "run, 0 citations" — an
 * empty list looks identical. We track `ranThisSession` so the empty state can
 * tell the two apart for the operator.
 *
 * `onError` is read through a ref so `fetchResults` / `run` keep a stable
 * identity even when the caller passes an inline callback — otherwise the seed
 * effect would re-fire on every render and loop GET forever.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../../lib/transport';
import {
  veridocApi,
  type VerificationResult,
  type VerificationSummary,
} from '../../lib/veridoc-api';

export type VerificationStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseSourceVerificationOptions {
  comparisonId: string | undefined;
  /** Only fetch while the view is actually active (4th tab selected). */
  enabled: boolean;
  /** lawyer / reviewer may trigger a run; viewer is read-only. */
  canRun: boolean;
  onError: (message: string) => void;
}

export interface UseSourceVerificationResult {
  status: VerificationStatus;
  results: VerificationResult[];
  /** Summary of the most recent run in this session (null until one runs). */
  lastRun: VerificationSummary | null;
  running: boolean;
  /** True once a run completed in this session (disambiguates the empty state). */
  ranThisSession: boolean;
  run: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useSourceVerification({
  comparisonId,
  enabled,
  canRun,
  onError,
}: UseSourceVerificationOptions): UseSourceVerificationResult {
  const [status, setStatus] = useState<VerificationStatus>('idle');
  const [results, setResults] = useState<VerificationResult[]>([]);
  const [lastRun, setLastRun] = useState<VerificationSummary | null>(null);
  const [running, setRunning] = useState<boolean>(false);
  const [ranThisSession, setRanThisSession] = useState<boolean>(false);

  // Latest onError without making it a dependency of the stable callbacks.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Guards against setting state after the comparison/view changed underneath us.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const fetchResults = useCallback(async (): Promise<void> => {
    if (!comparisonId) return;
    try {
      const res = await veridocApi.getSourceVerification(comparisonId);
      if (!aliveRef.current) return;
      setResults(res.results);
      setStatus('ready');
    } catch (err) {
      if (!aliveRef.current) return;
      setStatus('error');
      onErrorRef.current(errorMessage(err));
    }
  }, [comparisonId]);

  // Seed once the view is active. Re-seed when the comparison changes.
  useEffect(() => {
    if (!enabled || !comparisonId) return;
    setStatus('loading');
    void fetchResults();
  }, [enabled, comparisonId, fetchResults]);

  const run = useCallback(async (): Promise<void> => {
    if (!comparisonId || !canRun || running) return;
    setRunning(true);
    try {
      const summary = await veridocApi.runSourceVerification(comparisonId);
      if (!aliveRef.current) return;
      setLastRun(summary);
      setRanThisSession(true);
      await fetchResults();
    } catch (err) {
      if (!aliveRef.current) return;
      onErrorRef.current(errorMessage(err));
    } finally {
      if (aliveRef.current) setRunning(false);
    }
  }, [comparisonId, canRun, running, fetchResults]);

  return {
    status,
    results,
    lastRun,
    running,
    ranThisSession,
    run,
    refresh: fetchResults,
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return '当前角色无权发起来源核对。';
    if (err.status === 404) return '比对不存在或无访问权限。';
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}
